import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import { BoardMemberCard } from '../components/BoardMemberCard';
import { PageSeo } from '../components/PageSeo';
import { SubpageLayout } from '../components/SubpageLayout';
import { USE_STATIC_SITE_CONTENT } from '../config/staticSiteMode';
import {
    STATIC_BOARD_INFO,
    STATIC_BOARD_SECTIONS,
} from '../data/staticSite/content';
import { useAuth } from '../context/AuthContext';
import { mapFallbackSectionsFromI18n } from '../data/managementBoard';
import { stripHtmlForMeta, truncateMetaDescription } from '../utils/metaText';

const BOARD_CARD_SKELETON_COUNT = 4;

function ManagementBoardPageSkeleton({ statusLabel }) {
    return (
        <div role="status" aria-live="polite" aria-busy="true">
            <span className="visually-hidden">{statusLabel}</span>
            <SkeletonTheme
                baseColor="#e8ecf2"
                highlightColor="#f0f3f8"
            >
                <div className="management-board-rows">
                    <section
                        className="management-board-section management-board-section--skeleton"
                        aria-hidden
                    >
                        <div className="text-center mb-4">
                            <Skeleton
                                height={28}
                                width={260}
                                className="mx-auto"
                                borderRadius={8}
                            />
                        </div>
                        <div className="management-board-row management-board-row--skeleton">
                            {Array.from(
                                { length: BOARD_CARD_SKELETON_COUNT },
                                (_, i) => (
                                    <article
                                        key={`board-sk-${i}`}
                                        className="board-member-card board-member-card--skeleton"
                                    >
                                        <Skeleton
                                            height={48}
                                            width="100%"
                                            style={{
                                                borderRadius:
                                                    '1rem 1rem 0 0',
                                            }}
                                        />
                                        <div className="board-member-card-photo-wrap">
                                            <Skeleton
                                                height={200}
                                                width="100%"
                                                borderRadius={10}
                                            />
                                        </div>
                                        <div className="px-3 py-3">
                                            <Skeleton
                                                height={18}
                                                width="78%"
                                                className="mx-auto"
                                                borderRadius={6}
                                            />
                                        </div>
                                    </article>
                                ),
                            )}
                        </div>
                    </section>
                </div>
            </SkeletonTheme>
        </div>
    );
}

export function ManagementBoardPage() {
    const { t } = useTranslation();
    const { authToken } = useAuth();
    const [boardInfo, setBoardInfo] = useState(
        /** @type {null | { term: string, title: string, description: string | null }} */
        (USE_STATIC_SITE_CONTENT ? STATIC_BOARD_INFO : null),
    );
    /** `null` = yükleme sürüyor; `[]` = API boş/hata, i18n fallback kullanılacak */
    const [sections, setSections] = useState(
        USE_STATIC_SITE_CONTENT ? STATIC_BOARD_SECTIONS : null,
    );

    const fallbackSections = useMemo(() => {
        const raw = t('managementBoard.fallbackSections', { returnObjects: true });
        return mapFallbackSectionsFromI18n(raw);
    }, [t]);

    useEffect(() => {
        if (USE_STATIC_SITE_CONTENT) {
            setBoardInfo(STATIC_BOARD_INFO);
            setSections(STATIC_BOARD_SECTIONS);
            return undefined;
        }

        let cancelled = false;

        /*
        (async () => {
            try {
                const info = await fetchCmsBoardInfo({
                    accessToken: authToken,
                });
                if (!cancelled) setBoardInfo(info);
            } catch {
                if (!cancelled) setBoardInfo(null);
            }

            try {
                const rows = await fetchCmsBoardList({
                    accessToken: authToken,
                });
                const next = mapCmsBoardToPageSections(rows);
                if (cancelled) return;
                setSections(next.length > 0 ? next : []);
            } catch {
                if (!cancelled) setSections([]);
            }
        })();
        */

        return () => {
            cancelled = true;
        };
    }, [authToken]);

    const bannerTitle =
        boardInfo?.title?.trim() || t('managementBoard.title');
    const bannerEyebrow = boardInfo?.term?.trim() || undefined;
    const desc = boardInfo?.description?.trim();
    const bannerSubtitle = desc || t('managementBoard.subtitle');

    const loading = sections === null;
    const displaySections = USE_STATIC_SITE_CONTENT
        ? sections ?? []
        : !loading && sections.length > 0
          ? sections
          : fallbackSections;

    const boardMetaDesc = truncateMetaDescription(
        stripHtmlForMeta(bannerSubtitle) || t('seo.managementBoard'),
    );

    return (
        <>
            <PageSeo
                title={`${bannerTitle} — ${t('header.rotaryAlt')}`}
                description={boardMetaDesc}
            />
            <SubpageLayout
                title={bannerTitle}
                subtitle={bannerSubtitle}
                bannerEyebrow={bannerEyebrow}
                contentClassName="management-board-page"
            >
            {loading ? (
                <ManagementBoardPageSkeleton
                    statusLabel={t('managementBoard.loading')}
                />
            ) : (
                <div className="management-board-rows">
                    {displaySections.map((section) => (
                        <section
                            key={section.key}
                            className="management-board-section"
                            aria-labelledby={
                                section.sectionTitle
                                    ? `board-section-${section.key}`
                                    : undefined
                            }
                        >
                            {section.sectionTitle ? (
                                <h2
                                    id={`board-section-${section.key}`}
                                    className="management-board-section-title h5 text-center"
                                >
                                    {section.sectionTitle}
                                </h2>
                            ) : null}
                            <div className="management-board-row">
                                {section.members.map((member) => (
                                    <BoardMemberCard
                                        key={`${section.key}-${
                                            'memberId' in member &&
                                            member.memberId
                                                ? member.memberId
                                                : `${member.name}-${member.role}`
                                        }`}
                                        role={member.role}
                                        name={member.name}
                                        image={member.image}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </SubpageLayout>
        </>
    );
}
